/* eslint-disable no-param-reassign */
import { Dispatch } from 'redux';
import store from 'redux/store';
import { FormApi } from 'final-form';

import { getCustomerPaymentMethods } from 'redux/actions/customerReduxActions';
import API from 'services/api';
import showErrorAlert, { parseError } from 'services/errorHandler';
import { getBinDataObject, trackCardAbstraction } from 'utilities';
import { parseCardBrand } from 'utilities/card';
import * as CONSTS from 'redux/constants';
import { CATEGORIES, GA_ACTIONS, PMT_FIELD_NAME } from 'consts';
import { IPaymentMethodType } from 'models';

export const disableCardAbstraction = () => ({
  type: CONSTS.DISABLE_CARD_ABSTRACTION
});

export const editCardStart = () => ({
  type: CONSTS.EDIT_CARD_START
});

export const editCardSuccess = (oldCardId: string, newCard: Record<string, any>) => ({
  type: CONSTS.EDIT_CARD_SUCCESS,
  meta: { oldCardId, newCard }
});

export const editCardFail = (error: string) => ({
  type: CONSTS.EDIT_CARD_FAIL,
  payload: { error }
});

export const deleteCardStart = () => ({
  type: CONSTS.DELETE_CARD_START
});

export const deleteCardSuccess = (deletedCardId: string) => ({
  type: CONSTS.DELETE_CARD_SUCCESS,
  meta: { id: deletedCardId }
});

export const deleteCardFail = (error: string) => ({
  type: CONSTS.DELETE_CARD_FAIL,
  payload: { error }
});

const getPmyByBinStart = () => ({
  type: CONSTS.GET_PMT_BY_BIN_START
});

const getPmyByBinSuccess = (pmtByBin?: string) => ({
  type: CONSTS.GET_PMT_BY_BIN_SUCCESS,
  payload: pmtByBin
});

const getPmyByBinFail = () => ({
  type: CONSTS.GET_PMT_BY_BIN_FAIL
});

export const getPaymentMethodTypeByBin =
  ({
    bin,
    form,
    cardPMTS
  }: {
    bin: string;
    form: FormApi;
    cardPMTS: IPaymentMethodType[];
  }) =>
  async (dispatch: Dispatch<any>, getState: () => Record<string, any>) => {
    const { pageToken, merchant } = getState().global || {};
    const { selectedCurrency } = getState().paymentSettings || {};
    const { orgId } = merchant || {};

    const successCallback = (pmtType: string) => {
      dispatch(getPmyByBinSuccess(pmtType));
      form.change(PMT_FIELD_NAME, pmtType);
    };

    try {
      store.dispatch(getPmyByBinStart());

      const requestBody = {
        bin_data: getBinDataObject(bin),
        selected_currency: selectedCurrency,
        org_id: orgId
      };

      const response = await API.post(
        `hosted/payment_method_type_by_bin/${pageToken}`,
        requestBody
      );

      const pmtTypeByBin = response?.data?.data;

      trackCardAbstraction(GA_ACTIONS.SUCCESS);
      successCallback(pmtTypeByBin);
      return pmtTypeByBin;
    } catch (error) {
      const pmtTypeByBrand = tryMatchByBrand({ bin, cardPMTS });
      if (pmtTypeByBrand) {
        successCallback(pmtTypeByBrand);
        return pmtTypeByBrand;
      }
      store.dispatch(getPmyByBinFail());
      store.dispatch(disableCardAbstraction());
      form.change(PMT_FIELD_NAME, '');
      trackCardAbstraction(GA_ACTIONS.FAILURE);
    }
  };

const tryMatchByBrand = (params: {
  bin: string;
  cardPMTS: IPaymentMethodType[];
}): string | undefined => {
  const { bin, cardPMTS } = params;
  const { type: userTypedCardBrand } = parseCardBrand(bin);
  if (!userTypedCardBrand) return;
  let match = '';
  cardPMTS.forEach(cardPMT => {
    if (cardPMT?.mapping?.length) {
      const cardPMTBrands = cardPMT.mapping.reduce((acc, current) => {
        const { brand } = current || {};
        if (!brand) return acc;
        if (acc.includes(brand)) return acc;
        return [...acc, brand];
      }, [] as string[]);
      // @ts-ignore
      if (cardPMTBrands.includes(userTypedCardBrand)) {
        if (match) {
          match = '';
          return; // early return from the forEach
        }
        match = cardPMT.type || '';
      }
    }
  });
  return match;
};

export const editCard =
  ({
    pageToken,
    card,
    cardId,
    customerToken,
    onSuccess,
    onFailed
  }: {
    pageToken: string;
    card: any;
    cardId: string;
    customerToken: string;
    onSuccess: () => void;
    onFailed: () => void;
  }) =>
  async (dispatch: Dispatch<any>, getState: () => Record<string, any>) => {
    const { isPreview } = getState().global;
    if (isPreview) return;

    try {
      dispatch(editCardStart());

      const editCardResult = await API.post(
        `hosted/${pageToken}/payment_methods/${cardId}/${customerToken}`,
        card
      );

      dispatch(editCardSuccess(cardId, editCardResult.data?.data));
      if (onSuccess) onSuccess();

      dispatch(
        // @ts-ignore
        getCustomerPaymentMethods({
          pageToken,
          customerToken,
          category: CATEGORIES.card
        })
      );
    } catch (error) {
      showErrorAlert({ error, isCard: true });
      const { status } = parseError(error);
      const { error_code: errorCode } = status || {};
      dispatch(editCardFail(errorCode));
      if (onFailed) onFailed();
    }
  };

export const deleteCard =
  ({ cardToken, closeModal }: { cardToken: string; closeModal: () => void }) =>
  async (dispatch: Dispatch<any>, getState: () => Record<string, any>) => {
    const { isPreview, pageToken } = getState().global;
    if (isPreview) return;

    const { customerToken } = getState().customer;

    try {
      dispatch(deleteCardStart());

      await API.delete(
        `hosted/${pageToken}/payment_methods/${cardToken}/${customerToken}`
      );

      dispatch(deleteCardSuccess(cardToken));
      closeModal();

      dispatch(
        // @ts-ignore
        getCustomerPaymentMethods({
          pageToken,
          customerToken,
          category: CATEGORIES.card
        })
      );
    } catch (error) {
      showErrorAlert({ error, isCard: true });
      const { status } = parseError(error);
      const { error_code: errorCode } = status || {};
      dispatch(deleteCardFail(errorCode));
      closeModal();
    }
  };
